/**
 * Llits per revisar — runbook 10 (llits-1), decisió 5 i contracte C4.
 *
 * L'avís el calcula el backend en llegir (`bedWarning`); aquí només hi ha els textos de pantalla i
 * el filtre pur de la barra del calendari. Cap lògica d'avís al frontend (decisió 6).
 */

import type { BedType } from './BedIcon';
import type { BedConfigItem } from '@/hooks/useBedConfigMutation';

export type BedWarning = 'wants_twin_fixed_double' | 'wants_double_no_double' | 'mixed_preference';
export type BedConfigSource = 'manual' | 'auto';

/** Textos de C4: diuen la demanda, mai el nom del client. */
export const BED_WARNING_TEXT: Record<BedWarning, string> = {
  wants_twin_fixed_double: "Demana dos llits separats; l'habitació només té un llit de matrimoni",
  wants_double_no_double: "Demana llit de matrimoni; l'habitació no en té",
  mixed_preference: 'Preferència de llit mixta: cal mirar-la',
};

export const BED_AUTO_TEXT = 'Llits omplerts automàticament per la preferència del client';

/** Text de l'avís, o null si no n'hi ha (o si el backend en retorna un de desconegut). */
export function bedWarningText(warning: string | null | undefined): string | null {
  if (!warning) return null;
  return BED_WARNING_TEXT[warning as BedWarning] ?? null;
}

interface BedReviewFields {
  bedConfigSource?: BedConfigSource | null;
  bedWarning?: BedWarning | null;
  bedReviewPending?: boolean;
}

/**
 * Una reserva és «per revisar» quan ho diu el backend (`bedReviewPending`, runbook 20): estada oberta,
 * reserva viva i llits automàtics o un avís que ningú ha confirmat. «Confirmar» (font `manual`) tanca
 * la revisió; la ⚠ i la vora puntejada es queden a la cel·la com a informació (runbook 11, decisió 2).
 * Aquí no es mira cap data ni cap font: el dia d'avui el sap el servidor, no el navegador.
 */
export function needsBedReview(r: BedReviewFields): boolean {
  return r.bedReviewPending === true;
}

/** Filtre «Llits per revisar»: desactivat, retorna la mateixa llista. */
export function filterBedsToReview<T extends BedReviewFields>(items: T[], enabled: boolean): T[] {
  return enabled ? items.filter(needsBedReview) : items;
}

/** Converteix [{type,qty}] → array expandit ['double','single'] */
export function itemsToBeds(items: BedConfigItem[]): BedType[] {
  const result: BedType[] = [];
  for (const item of items) {
    for (let i = 0; i < item.qty; i++) {
      result.push(item.type);
    }
  }
  return result;
}

/** Converteix array expandit ['double','single','crib'] → [{type,qty}] */
export function bedsToItems(beds: BedType[]): BedConfigItem[] {
  const counts: Partial<Record<BedType, number>> = {};
  for (const bed of beds) {
    counts[bed] = (counts[bed] ?? 0) + 1;
  }
  return Object.entries(counts).map(([type, qty]) => ({
    type: type as BedType,
    qty: qty as number,
  }));
}
